// Client-side store for the patient-portal session token. The token
// is the only credential we send to the backend (in
// `Authorization: Bearer <token>`); backend resolves it against
// `PortalSession`.
//
// Keep bearer credentials in sessionStorage rather than localStorage so
// a stolen browser profile or later browser restart does not retain
// active patient credentials. A future pass can move this to httpOnly
// cookies.

const TOKEN_KEY = 'healthcom-schedule.portalSession.token';
const PATIENT_KEY = 'healthcom-schedule.portalSession.patient';
const EXPIRES_KEY = 'healthcom-schedule.portalSession.expiresAt';

export type StoredPatientContext = {
  patientId: string;
  clinicId: number;
  fname?: string | null;
  lname?: string | null;
};

export const portalSession = {
  getToken(): string | null {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },

  setToken(token: string, expiresAt?: string) {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
      if (expiresAt) sessionStorage.setItem(EXPIRES_KEY, expiresAt);
    } catch {
      /* storage blocked — sign-in won't persist across reloads */
    }
  },

  getExpiresAt(): string | null {
    try {
      return sessionStorage.getItem(EXPIRES_KEY);
    } catch {
      return null;
    }
  },

  getPatient(): StoredPatientContext | null {
    try {
      const raw = sessionStorage.getItem(PATIENT_KEY);

      return raw ? (JSON.parse(raw) as StoredPatientContext) : null;
    } catch {
      return null;
    }
  },

  setPatient(patient: StoredPatientContext) {
    try {
      sessionStorage.setItem(PATIENT_KEY, JSON.stringify(patient));
    } catch {
      /* ignore */
    }
  },

  clear() {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(PATIENT_KEY);
      sessionStorage.removeItem(EXPIRES_KEY);
    } catch {
      /* ignore */
    }
  },

  // Best-effort liveness check. The wrapper still validates server-
  // side; this is just to avoid sending a clearly-expired token from
  // the client when we already know better.
  isLikelyValid(): boolean {
    const token = this.getToken();

    if (!token) return false;

    const expiresAt = this.getExpiresAt();

    if (!expiresAt) return true;

    const expiresAtMs = Date.parse(expiresAt);

    return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();
  },
};
